import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

// Licencia SST (Res. 908/2025): credencial de la PERSONA, compartida entre sus empresas.
export type MyLicencia = components['schemas']['MyLicenciaRead']
export type EmployeeLicencia = components['schemas']['EmployeeLicenciaRead']
export type LicenciaCategoria = components['schemas']['LicenciaCategoria']
type LicenciaUploadTarget = components['schemas']['LicenciaUploadTargetRead']
export type LicenciaQueueItem = components['schemas']['LicenciaQueueRead']

export const LICENCIA_QUEUE_PAGE_SIZE = 20

export function useMyLicencia(enabled = true) {
  return useQuery({
    queryKey: ['me-licencia'],
    queryFn: async () => (await api.get<MyLicencia>('/me/licencia')).data,
    enabled,
  })
}

/** Las respuestas de minteo de me, admin y partner son esquemas distintos con la misma forma. */
type UploadTargetLike = {method: string; object_key: string; url: string}

export async function mintLicenciaUploadTarget<T extends UploadTargetLike = LicenciaUploadTarget>(
  basePath: string,
  file: File
): Promise<T> {
  return (
    await api.post<T>(`${basePath}/uploads`, {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  ).data
}

/** `/me/licencia` queda fuera a propósito: no tiene `userId` ni verificación (ver `useSaveMyLicencia`). */
export function useLicenciaMutations<TLicencia, TSaveInput extends {userId: string}>(
  basePathForUserId: (userId: string) => string,
  invalidate: () => void
) {
  const save = useMutation({
    mutationFn: async ({userId, ...input}: TSaveInput) =>
      (await api.put<TLicencia>(basePathForUserId(userId), input)).data,
    onSuccess: invalidate,
  })
  const verify = useMutation({
    mutationFn: async (userId: string) => (await api.post<TLicencia>(`${basePathForUserId(userId)}/verificar`)).data,
    onSuccess: invalidate,
  })
  return {save, verify}
}

export function useSaveMyLicencia() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {numero: string; categoria: LicenciaCategoria; vigencia: string; doc_key: string}) =>
      (await api.put<MyLicencia>('/me/licencia', input)).data,
    onSuccess: (data) => queryClient.setQueryData(['me-licencia'], data),
  })
}

// Vista de empresa: categoría y estado, sin `download_url` a propósito (identity-access-spec).
export function useEmployeeLicencia(businessId: string | undefined, employeeId: string | null) {
  return useQuery({
    queryKey: ['employee-licencia', businessId, employeeId],
    queryFn: async () =>
      (await api.get<EmployeeLicencia>(`/businesses/${businessId}/employees/${employeeId}/licencia`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

export function useLicenciaQueue(params: {page: number; search: string}, enabled = true) {
  return useQuery({
    queryKey: ['licencia-queue', params],
    queryFn: async () =>
      (
        await api.get<Page<LicenciaQueueItem>>('/admin/licencias', {
          params: {page: params.page, page_size: LICENCIA_QUEUE_PAGE_SIZE, search: params.search || undefined},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

function useLicenciaQueueInvalidation() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: ['licencia-queue']})
}

export function useVerifyLicencia() {
  const invalidate = useLicenciaQueueInvalidation()
  return useMutation({
    mutationFn: async (userId: string) => api.post(`/admin/licencias/${userId}/verificar`),
    onSuccess: invalidate,
  })
}

export function useRejectLicencia() {
  const invalidate = useLicenciaQueueInvalidation()
  return useMutation({
    mutationFn: async ({userId, motivo}: {userId: string; motivo: string}) =>
      api.post(`/admin/licencias/${userId}/rechazar`, {motivo}),
    onSuccess: invalidate,
  })
}
